import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { ArrowLeft, Calendar, Plane, Building2, Car, Ticket, Trash2, Pencil, Download, Mail, Share2, FolderInput, Scissors, Ban, RotateCcw, Plus, Radio } from "lucide-react";
import { toast } from "sonner";
import EditReservationDialog from "../components/EditReservationDialog";
import AddReservationDialog from "../components/AddReservationDialog";
import EditTripDialog from "../components/EditTripDialog";
import MoveReservationDialog from "../components/MoveReservationDialog";
import SplitTripDialog from "../components/SplitTripDialog";
import TripBudget from "../components/budget/TripBudget";
import TodayCockpit from "../components/home/TodayCockpit";
import { LiveStatusPill, LiveGateRow } from "../components/reservations/LiveStatusPill";

function fmtDate(iso) {
  if (!iso) return "—";
  const s = iso.replace("Z", "+00:00");
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function PersonChip({ person, email }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sand-900">
      {person.name || "—"}
      {person.seat && <span className="text-sand-500">· seat {person.seat}</span>}
      {email && (
        <span
          className="inline-flex items-center gap-1 text-[#5E8467] text-[10px] uppercase tracking-widest font-mono ml-1"
          title={`Will be invited as ${email}`}
        >
          <Mail size={11} /> invited
        </span>
      )}
    </span>
  );
}

function ResActions({ res, onEdit, onDelete, onMove, onCancel }) {
  const cancelled = res.booking_status === "cancelled";
  return (
    <div className="flex items-center gap-1">
      {cancelled && (
        <span className="font-mono text-[10px] uppercase tracking-wide bg-red-100 text-error px-2 py-0.5 mr-1" data-testid={`cancelled-badge-${res.id}`}>
          Cancelled
        </span>
      )}
      <button onClick={() => onMove(res)} className="p-2 hover:bg-sand-100 text-sand-700 hover:text-ember" title="Move to another trip" data-testid={`move-${res.id}`}>
        <FolderInput size={14} />
      </button>
      <button onClick={() => onEdit(res)} className="p-2 hover:bg-sand-100" title="Edit" data-testid={`edit-${res.id}`}>
        <Pencil size={14} />
      </button>
      <button onClick={() => onCancel(res)} className="p-2 hover:bg-amber-50 text-sand-700 hover:text-amber-600" title={cancelled ? "Restore reservation" : "Mark cancelled"} data-testid={`cancel-${res.id}`}>
        {cancelled ? <RotateCcw size={14} /> : <Ban size={14} />}
      </button>
      <button onClick={() => onDelete(res)} className="p-2 hover:bg-red-50 text-sand-700 hover:text-error" title="Delete" data-testid={`delete-${res.id}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function FlightItem({ res, onEdit, onDelete, onMove, onCancel }) {
  const f = res.flight || {};
  const [live, setLive] = useState(null);
  const flightNum = (f.flight_number || "").replace(/\s+/g, "");
  const depDate = f.departure_time ? String(f.departure_time).slice(0, 10) : null;
  useEffect(() => {
    if (!flightNum || !depDate) return;
    let alive = true;
    api.get(`/flight-status/${flightNum}/${depDate}`)
      .then((r) => { if (alive) setLive(r?.data?.status || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [flightNum, depDate]);

  const passengers = (f.passengers && f.passengers.length > 0)
    ? f.passengers
    : (f.passenger_name ? [{ name: f.passenger_name, seat: f.seat }] : []);
  return (
    <div className="timeline-item" data-testid={`reservation-${res.id}`}>
      <div className={"bg-surface rounded-card border border-hairline p-6" + (res.booking_status === "cancelled" ? " opacity-60" : "")}>
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Plane size={16} className="text-ember" />
            <span className="label-tag">Flight</span>
            {f.flight_number && (
              <span className="font-mono text-xs bg-sand-900 text-white px-2 py-0.5">
                {f.airline || ""} {f.flight_number}
              </span>
            )}
            {res.booking_status === "planned" && (
              <span className="font-mono text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`planned-badge-${res.id}`}>
                Planned — not booked
              </span>
            )}
            <LiveStatusPill live={live} tone="light" />
          </div>
          <ResActions res={res} onEdit={onEdit} onDelete={onDelete} onMove={onMove} onCancel={onCancel} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
          <div>
            <p className="label-tag mb-1">Departure</p>
            <p className="font-display font-medium text-2xl tracking-tight">{f.departure_airport || f.departure_city || "—"}</p>
            <p className="text-xs text-sand-600 mt-1">{f.departure_city || ""}</p>
            <p className="text-sm font-mono mt-2">{fmtDate(f.departure_time)}</p>
          </div>
          <div>
            <p className="label-tag mb-1">Arrival</p>
            <p className="font-display font-medium text-2xl tracking-tight">{f.arrival_airport || f.arrival_city || "—"}</p>
            <p className="text-xs text-sand-600 mt-1">{f.arrival_city || ""}</p>
            <p className="text-sm font-mono mt-2">{fmtDate(f.arrival_time)}</p>
          </div>
        </div>

        {live ? <LiveGateRow live={live} tone="light" /> : null}

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-sand-600 pt-4 border-t border-sand-200">
          {f.confirmation_number && <span>CONF · <span className="font-mono text-sand-900">{f.confirmation_number}</span></span>}
          {f.cabin && <span>CABIN · <span className="font-mono text-sand-900">{f.cabin}</span></span>}
        </div>

        {passengers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-sand-100">
            <p className="label-tag mb-2">Passengers</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
              {passengers.map((p, i) => (
                <PersonChip key={i} person={p} email={(res.attendee_emails || {})[p.name]} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HotelItem({ res, onEdit, onDelete, onMove, onCancel }) {
  const h = res.hotel || {};
  return (
    <div className="timeline-item" data-testid={`reservation-${res.id}`}>
      <div className={"bg-surface rounded-card border border-hairline p-6" + (res.booking_status === "cancelled" ? " opacity-60" : "")}>
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-center gap-3">
            <Building2 size={16} className="text-ember" />
            <span className="label-tag">Hotel</span>
          </div>
          <ResActions res={res} onEdit={onEdit} onDelete={onDelete} onMove={onMove} onCancel={onCancel} />
        </div>

        <h3 className="heading-h3 text-2xl mb-3">{h.hotel_name || "Hotel stay"}</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
          <div>
            <p className="label-tag mb-1">Check-in</p>
            <p className="text-sm font-mono">{fmtDate(h.check_in)}</p>
          </div>
          <div>
            <p className="label-tag mb-1">Check-out</p>
            <p className="text-sm font-mono">{fmtDate(h.check_out)}</p>
          </div>
        </div>

        {(h.address || h.city) && (
          <p className="text-sm text-sand-700 mb-2">
            {[h.address, h.city, h.country].filter(Boolean).join(", ")}
          </p>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-sand-600 pt-4 border-t border-sand-200">
          {h.confirmation_number && <span>CONF · <span className="font-mono text-sand-900">{h.confirmation_number}</span></span>}
          {h.room_type && <span>ROOM · <span className="text-sand-900">{h.room_type}</span></span>}
          {h.phone && <span>TEL · <span className="font-mono text-sand-900">{h.phone}</span></span>}
        </div>

        {(() => {
          const guests = (h.guests && h.guests.length > 0)
            ? h.guests
            : (h.guest_name ? [{ name: h.guest_name }] : []);
          return guests.length > 0 ? (
            <div className="mt-3 pt-3 border-t border-sand-100">
              <p className="label-tag mb-2">Guests</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                {guests.map((g, i) => <PersonChip key={i} person={g} email={(res.attendee_emails || {})[g.name]} />)}
              </div>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
}

function CarItem({ res, onEdit, onDelete, onMove, onCancel }) {
  const c = res.car || {};
  const drivers = (c.drivers && c.drivers.length > 0)
    ? c.drivers
    : (c.driver_name ? [{ name: c.driver_name }] : []);
  return (
    <div className="timeline-item" data-testid={`reservation-${res.id}`}>
      <div className={"bg-surface rounded-card border border-hairline p-6" + (res.booking_status === "cancelled" ? " opacity-60" : "")}>
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-center gap-3">
            <Car size={16} className="text-ember" />
            <span className="label-tag">Car rental</span>
            {c.vendor && (
              <span className="font-mono text-xs bg-sand-900 text-white px-2 py-0.5">
                {c.vendor}
              </span>
            )}
          </div>
          <ResActions res={res} onEdit={onEdit} onDelete={onDelete} onMove={onMove} onCancel={onCancel} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
          <div>
            <p className="label-tag mb-1">Pickup</p>
            <p className="font-display font-medium text-lg tracking-tight">{c.pickup_location || "—"}</p>
            {c.pickup_address && <p className="text-xs text-sand-600 mt-1">{c.pickup_address}</p>}
            <p className="text-sm font-mono mt-2">{fmtDate(c.pickup_time)}</p>
          </div>
          <div>
            <p className="label-tag mb-1">Dropoff</p>
            <p className="font-display font-medium text-lg tracking-tight">{c.dropoff_location || c.pickup_location || "—"}</p>
            {c.dropoff_address && <p className="text-xs text-sand-600 mt-1">{c.dropoff_address}</p>}
            <p className="text-sm font-mono mt-2">{fmtDate(c.dropoff_time)}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-sand-600 pt-4 border-t border-sand-200">
          {c.confirmation_number && <span>CONF · <span className="font-mono text-sand-900">{c.confirmation_number}</span></span>}
          {c.vehicle_class && <span>CLASS · <span className="text-sand-900">{c.vehicle_class}</span></span>}
          {c.phone && <span>TEL · <span className="font-mono text-sand-900">{c.phone}</span></span>}
        </div>

        {drivers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-sand-100">
            <p className="label-tag mb-2">Drivers</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
              {drivers.map((d, i) => <PersonChip key={i} person={d} email={(res.attendee_emails || {})[d.name]} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EventItem({ res, onEdit, onDelete, onMove, onCancel }) {
  const ev = res.event || {};
  const attendees = (ev.attendees && ev.attendees.length > 0)
    ? ev.attendees
    : (ev.attendee_name ? [{ name: ev.attendee_name, seat: ev.seat }] : []);
  return (
    <div className="timeline-item" data-testid={`reservation-${res.id}`}>
      <div className={"bg-surface rounded-card border border-hairline p-6" + (res.booking_status === "cancelled" ? " opacity-60" : "")}>
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex items-center gap-3">
            <Ticket size={16} className="text-ember" />
            <span className="label-tag">Event{ev.event_type ? ` · ${ev.event_type}` : ""}</span>
          </div>
          <ResActions res={res} onEdit={onEdit} onDelete={onDelete} onMove={onMove} onCancel={onCancel} />
        </div>

        <h3 className="heading-h3 text-2xl mb-3">{ev.event_name || "Event"}</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
          <div>
            <p className="label-tag mb-1">Starts</p>
            <p className="text-sm font-mono">{fmtDate(ev.start_time)}</p>
          </div>
          {ev.end_time && (
            <div>
              <p className="label-tag mb-1">Ends</p>
              <p className="text-sm font-mono">{fmtDate(ev.end_time)}</p>
            </div>
          )}
        </div>

        {(ev.venue_name || ev.address || ev.city) && (
          <p className="text-sm text-sand-700 mb-2">
            {ev.venue_name && <span className="font-display font-semibold">{ev.venue_name}</span>}
            {ev.venue_name && (ev.address || ev.city) && " · "}
            {[ev.address, ev.city, ev.country].filter(Boolean).join(", ")}
          </p>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-sand-600 pt-4 border-t border-sand-200">
          {ev.confirmation_number && <span>CONF · <span className="font-mono text-sand-900">{ev.confirmation_number}</span></span>}
          {ev.seat && <span>SEAT · <span className="font-mono text-sand-900">{ev.seat}</span></span>}
          {ev.phone && <span>TEL · <span className="font-mono text-sand-900">{ev.phone}</span></span>}
        </div>

        {attendees.length > 0 && (
          <div className="mt-3 pt-3 border-t border-sand-100">
            <p className="label-tag mb-2">Attendees</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
              {attendees.map((a, i) => <PersonChip key={i} person={a} email={(res.attendee_emails || {})[a.name]} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingTrip, setEditingTrip] = useState(false);
  const [moving, setMoving] = useState(null);
  const [splitting, setSplitting] = useState(false);
  const [light, setLight] = useState(null);
  const [homeState, setHomeState] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/trips/${tripId}`);
      setData(data);
    } catch (e) {
      toast.error("Trip not found");
      navigate("/home");
    }
  };

  useEffect(() => { load(); }, [tripId]);

  useEffect(() => {
    api.get(`/trips/${tripId}/light`).then(({ data }) => setLight(data)).catch(() => {});
  }, [tripId]);

  useEffect(() => {
    api.get("/home/state").then(({ data }) => setHomeState(data)).catch(() => {});
  }, []);

  const handleDeleteRes = async (res) => {
    if (!window.confirm("Delete this reservation?")) return;
    try {
      await api.delete(`/trips/${tripId}/reservations/${res.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleCancelRes = async (res) => {
    const cancelling = res.booking_status !== "cancelled";
    try {
      await api.post(`/trips/${tripId}/reservations/${res.id}/cancel`, { cancelled: cancelling });
      toast.success(cancelling ? "Marked cancelled" : "Reservation restored");
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDeleteTrip = async () => {
    if (!window.confirm("Delete this entire trip and all reservations?")) return;
    try {
      await api.delete(`/trips/${tripId}`);
      toast.success("Trip deleted");
      navigate("/home");
    } catch (e) {
      toast.error("Failed to delete trip");
    }
  };

  const handleShare = async () => {
    const tripName = data?.trip?.name || "My trip";
    const shareUrl = window.location.href;
    const dateStr = data?.trip?.start_date && data?.trip?.end_date
      ? `${data.trip.start_date} → ${data.trip.end_date}`
      : (data?.trip?.start_date || "");
    const text = `${tripName}${dateStr ? " · " + dateStr : ""}\n${reservations.length} reservation${reservations.length !== 1 ? "s" : ""}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: tripName, text, url: shareUrl });
      } catch (err) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Sharing not supported on this device");
      }
    }
  };

  const handleExport = () => {
    const url = `${API_BASE}/trips/${tripId}/ics`;
    // Use a fetch with credentials to get auth, then trigger download
    api.get(`/trips/${tripId}/ics`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/calendar" });
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `${(data?.trip?.name || "trip").replace(/[^a-z0-9-_]/gi, "_")}.ics`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
        toast.success("Calendar file downloaded");
      })
      .catch((e) => {
        const detail = e?.response?.data;
        toast.error(typeof detail === "string" ? detail : "Could not export");
      });
  };

  if (!data) {
    return (
      <div>
          <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10">
          <div className="animate-pulse h-32 bg-sand-100" />
        </main>
      </div>
    );
  }

  const { trip, reservations } = data;

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16 pb-32 md:pb-16" data-testid="trip-detail">
        <Link to="/home" className="inline-flex items-center text-sm text-sand-600 hover:text-ember mb-8" data-testid="back-to-dashboard">
          <ArrowLeft size={14} className="mr-2" /> All trips
        </Link>

        {homeState?.state === "live" && homeState.active_trip_id === tripId && (
          <TodayCockpit home={homeState} variant="compact" />
        )}

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="label-tag mb-3">Trip</p>
            <h1 className="heading-h1 text-4xl sm:text-5xl mb-2" data-testid="trip-name">{trip.name}</h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm text-sand-600">
              {trip.destination && <span className="font-mono">📍 {trip.destination}</span>}
              {trip.start_date && (
                <span className="inline-flex items-center gap-2 font-mono">
                  <Calendar size={14} /> {trip.start_date} {trip.end_date && trip.end_date !== trip.start_date ? `→ ${trip.end_date}` : ""}
                </span>
              )}
              <span className="font-mono">{reservations.length} reservation{reservations.length !== 1 ? "s" : ""}</span>
              {light?.available && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[11px] tracking-wide"
                  style={{ background: "var(--hour-soft)", color: "var(--hour-deep)" }}
                  title={`Golden hour${light.place ? ` in ${light.place}` : ""} on ${light.date} · sunrise ${light.sunrise} · sunset ${light.sunset} (${light.tz})`}
                  data-testid="golden-hour-chip"
                >
                  ◐ BEST LIGHT {light.golden_start}–{light.golden_end}
                </span>
              )}
              {trip.entity && (
                <span className="font-mono text-[10px] uppercase tracking-wide bg-sand-900 text-white px-2 py-0.5" data-testid="trip-entity-badge">
                  {trip.entity}{trip.billable ? " · billable" : ""}
                </span>
              )}
            </div>
          </div>

          {/* Desktop action bar */}
          <div className="hidden md:flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`/trips/${tripId}/live`)}
              className="btn-swiss btn-outline text-sm"
              data-testid="live-view-btn"
            >
              <Radio size={14} className="mr-2" /> Live view
            </button>
            <button
              onClick={() => setAdding(true)}
              className="btn-swiss btn-outline text-sm"
              data-testid="add-item-btn"
            >
              <Plus size={14} className="mr-2" /> Add item
            </button>
            <button
              onClick={() => setEditingTrip(true)}
              className="btn-swiss btn-ghost text-sm"
              data-testid="edit-trip-btn"
            >
              <Pencil size={14} className="mr-2" /> Edit trip
            </button>
            {reservations.length >= 2 && (
              <button
                onClick={() => setSplitting(true)}
                className="btn-swiss btn-ghost text-sm"
                data-testid="split-trip-btn"
              >
                <Scissors size={14} className="mr-2" /> Split
              </button>
            )}
            <button
              onClick={handleShare}
              className="btn-swiss btn-ghost text-sm"
              data-testid="share-trip-btn"
            >
              <Share2 size={14} className="mr-2" /> Share
            </button>
            <button
              onClick={handleExport}
              className="btn-swiss btn-primary text-sm min-w-[140px]"
              data-testid="export-ics-btn"
              disabled={reservations.length === 0}
            >
              <Download size={14} className="mr-2" /> Export .ics
            </button>
            <button
              onClick={handleDeleteTrip}
              className="btn-swiss btn-outline text-sm"
              data-testid="delete-trip-btn"
            >
              <Trash2 size={14} className="mr-2" /> Delete
            </button>
          </div>
        </div>

        {reservations.length === 0 ? (
          <div className="rounded-card border border-hairline bg-surface p-12 text-center">
            <p className="text-sand-600 mb-4">No reservations yet for this trip.</p>
            <button onClick={() => setAdding(true)} className="btn-swiss btn-primary text-sm" data-testid="empty-add-item-btn">
              <Plus size={14} className="mr-2" /> Add a flight, hotel, car or event
            </button>
          </div>
        ) : (
          <div className="timeline-rail" data-testid="trip-timeline">
            {reservations.map((r) => {
              if (r.type === "flight") return <FlightItem key={r.id} res={r} onEdit={setEditing} onDelete={handleDeleteRes} onMove={setMoving} onCancel={handleCancelRes} />;
              if (r.type === "hotel")  return <HotelItem  key={r.id} res={r} onEdit={setEditing} onDelete={handleDeleteRes} onMove={setMoving} onCancel={handleCancelRes} />;
              if (r.type === "car")    return <CarItem    key={r.id} res={r} onEdit={setEditing} onDelete={handleDeleteRes} onMove={setMoving} onCancel={handleCancelRes} />;
              if (r.type === "event")  return <EventItem  key={r.id} res={r} onEdit={setEditing} onDelete={handleDeleteRes} onMove={setMoving} onCancel={handleCancelRes} />;
              return null;
            })}
          </div>
        )}

        <TripBudget tripId={tripId} />

        {adding && (
          <AddReservationDialog
            tripId={tripId}
            onClose={() => setAdding(false)}
            onAdded={() => { setAdding(false); load(); }}
          />
        )}

        {editing && (
          <EditReservationDialog
            reservation={editing}
            tripId={tripId}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {editingTrip && (
          <EditTripDialog
            trip={trip}
            onClose={() => setEditingTrip(false)}
            onSaved={() => { setEditingTrip(false); load(); }}
          />
        )}

        {moving && (
          <MoveReservationDialog
            reservation={moving}
            tripId={tripId}
            isOnlyReservation={reservations.length === 1}
            onClose={() => setMoving(null)}
            onMoved={({ tripId: target, sourceDeleted }) => {
              setMoving(null);
              if (sourceDeleted || target !== tripId) navigate(`/trips/${target}`);
              else load();
            }}
          />
        )}

        {splitting && (
          <SplitTripDialog
            trip={trip}
            reservations={reservations}
            onClose={() => setSplitting(false)}
            onSplit={(newTrip) => {
              setSplitting(false);
              navigate(`/trips/${newTrip.id}`);
            }}
          />
        )}
      </main>

      {/* Sticky mobile action bar */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-sand-200 px-4 py-3 flex gap-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 12px)" }}
        data-testid="mobile-action-bar"
      >
        <button
          onClick={() => setAdding(true)}
          className="btn-swiss btn-ghost text-xs flex-1 justify-center"
          data-testid="add-item-btn-mobile"
          aria-label="Add item"
        >
          <Plus size={14} />
        </button>
        <button
          onClick={() => setEditingTrip(true)}
          className="btn-swiss btn-ghost text-xs flex-1 justify-center"
          data-testid="edit-trip-btn-mobile"
          aria-label="Edit trip"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={handleShare}
          className="btn-swiss btn-ghost text-xs flex-1 justify-center"
          data-testid="share-trip-btn-mobile"
          aria-label="Share trip"
        >
          <Share2 size={14} />
        </button>
        <button
          onClick={handleDeleteTrip}
          className="btn-swiss btn-ghost text-xs flex-1 justify-center text-sand-600 hover:text-error"
          data-testid="delete-trip-btn-mobile"
          aria-label="Delete trip"
        >
          <Trash2 size={14} />
        </button>
        <button
          onClick={handleExport}
          className="btn-swiss btn-primary text-sm flex-[2] justify-center"
          data-testid="export-ics-btn-mobile"
          disabled={reservations.length === 0}
        >
          <Download size={14} className="mr-2" /> Export .ics
        </button>
      </div>
    </div>
  );
}
